'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { SectionHeader, PAGE_CONTAINER } from './ui';
import type { FaqItem } from '@/lib/api';
import { useInView } from '@/hooks/useInView';
import { useTranslations } from 'next-intl';
import { TALENT_FAQ as TALENT } from '@/lib/exam-talent';

interface HomepageFAQProps {
  faqs: FaqItem[];
}

export function HomepageFAQ({ faqs }: HomepageFAQProps) {
  const t = useTranslations('home.faq');
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [sectionRef, inView] = useInView<HTMLElement>({ threshold: 0.05 });

  if (faqs.length === 0) return null;

  return (
    <section ref={sectionRef} className="bg-pattern overflow-hidden py-12 lg:py-16 bg-slate-50 [--pattern-color:rgb(13_148_136_/_0.05)]">
      {/* Mobile reads heading, questions, help link. From `lg` the heading and
          help link stack on the left and the questions fill the right, so the
          section uses the page width instead of one narrow centred column.
          The 1fr row takes the spare height, keeping the link under the heading. */}
      <div className={`${PAGE_CONTAINER} grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:grid-rows-[auto_1fr] lg:gap-x-12 lg:gap-y-3`}>
        <div className={`transition-all duration-600 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <SectionHeader
            badge={t('badge')}
            badgeVariant="teal"
            title={t('title')}
            centered={false}
            className="mb-0"
          />
        </div>

        <div className="space-y-2 lg:col-start-2 lg:row-span-2 lg:row-start-1">
          {faqs.map((faq, i) => (
            <div
              key={faq.id}
              style={{ transitionDelay: `${i * 60}ms` }}
              className={`bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-500 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="text-sm font-semibold text-slate-900 pr-4">{faq.question}</span>
                <ChevronDown
                  className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* lg:pb-[258px] reserves the figure's height (300px less the section's 56px bottom
            padding) so the section grows to fit it instead of the figure covering this link. */}
        <p className="text-sm text-slate-500 lg:col-start-1 lg:row-start-2 lg:pb-[258px]">
          {t('stillHaveQuestions')}{' '}
          <Link href="/help" className="text-teal-600 hover:underline font-medium">
            {t('helpCenter')}
          </Link>
        </p>
      </div>

      {/* Anchored to the section, not the grid cell, so the crop line is the
          section's bottom edge. The wrapper takes the container's width and
          gutters so the figure lines up with the heading column. */}
      <div className={`${PAGE_CONTAINER} pointer-events-none absolute inset-x-0 bottom-0 max-lg:hidden`}>
        <Image
          src={TALENT.src}
          width={TALENT.width}
          height={TALENT.height}
          sizes="184px"
          alt={t('talentAlt')}
          className="block h-[300px] w-auto"
        />
      </div>
    </section>
  );
}
